import { Box, Fade, Link, Typography, useTheme } from '@mui/material';
import { type JSX } from 'react';

type MemberIdentityHeaderProps = {
    /** Small uppercase label above the name, e.g. "Logged in as". */
    label: string;
    /** Full member name; may be empty (falls back to the email as the headline). */
    name: string;
    email: string;
    logoutText: string;
    onLogout: () => void;
};

function initialsFrom(text: string): string {
    const parts = text.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const first = parts[0]!.charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1]!.charAt(0) : '';
    return (first + last).toUpperCase();
}

/**
 * Dumb identity confirmation row for the native member-card flow: shows which
 * member a purchase will be attached to, with a low-emphasis "log out" to switch
 * accounts. Styled entirely from the tenant MUI theme so it reads in light + dark.
 */
export default function MemberIdentityHeader({
    label,
    name,
    email,
    logoutText,
    onLogout,
}: MemberIdentityHeaderProps): JSX.Element {
    const theme = useTheme();
    const trimmedName = name.trim();
    const headline = trimmedName || email;
    const showEmail = trimmedName.length > 0 && email.length > 0;

    return (
        <Fade in appear timeout={350}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: theme.spacing(1.5),
                    paddingBottom: theme.spacing(2),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    aria-hidden="true"
                    sx={{
                        flexShrink: 0,
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        backgroundColor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        lineHeight: 1,
                    }}
                >
                    {initialsFrom(headline)}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: theme.palette.text.secondary,
                        }}
                    >
                        {label}
                    </Typography>
                    <Typography
                        sx={{
                            fontWeight: 700,
                            lineHeight: 1.25,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {headline}
                    </Typography>
                    {showEmail && (
                        <Typography
                            variant="body2"
                            sx={{
                                color: theme.palette.text.secondary,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {email}
                        </Typography>
                    )}
                </Box>
                <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={onLogout}
                    sx={{ flexShrink: 0, color: theme.palette.text.secondary }}
                >
                    {logoutText}
                </Link>
            </Box>
        </Fade>
    );
}
